import React from 'react';
import { Droplets, Shield, AlertTriangle, CheckCircle } from 'lucide-react';

interface Props {
  primaryColor?: string;
}

export default function DrainPanTreatmentSection({ primaryColor = '#0066CC' }: Props) {
  return (
    <section className="py-16 px-4 bg-gradient-to-br from-blue-50 to-cyan-50">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: The Problem */}
          <div>
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4 shadow-sm">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <span className="font-semibold text-gray-900">Florida's #1 AC Killer</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Zoogleal Slime: The Silent System Destroyer
            </h2>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Florida's humidity creates a perfect breeding ground for <strong>zoogleal slime</strong> — a biological growth that forms in your AC's condensate drain pan. This thick, slimy buildup clogs drain lines, triggers safety switches, and can shut down your entire system.
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-gray-700">
                  <strong>Water damage</strong> to ceilings, walls, and floors
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-gray-700">
                  <strong>System shutdowns</strong> from safety switch activation
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-gray-700">
                  <strong>Mold and mildew growth</strong> risk throughout your home
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-gray-700">
                  <strong>Emergency service calls</strong> when you least expect it
                </p>
              </div>
            </div>
          </div>

          {/* Right: The Solution */}
          <div className="relative">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
              <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6"
                style={{ backgroundColor: `${primaryColor}14`, color: primaryColor }}
              >
                <Shield className="w-8 h-8" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                EPA-Approved 12-Month Protection
              </h3>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Every Comfort Club membership includes a <strong>12-month time-release drain pan treatment</strong>. This EPA-approved tablet slowly releases anti-microbial protection for an entire year — preventing zoogleal slime before it starts.
              </p>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 shrink-0 mt-0.5" style={{ color: primaryColor }} />
                  <p className="text-gray-700">
                    <strong>Installed during every tune-up</strong> — set it and forget it for 12 months
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 shrink-0 mt-0.5" style={{ color: primaryColor }} />
                  <p className="text-gray-700">
                    <strong>Prevents clogs and shutdowns</strong> before they happen
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 shrink-0 mt-0.5" style={{ color: primaryColor }} />
                  <p className="text-gray-700">
                    <strong>Included with every membership</strong> — no extra charge
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 shrink-0 mt-0.5" style={{ color: primaryColor }} />
                  <p className="text-gray-700">
                    <strong>Safe for pets, family, and the environment</strong>
                  </p>
                </div>
              </div>

              <div
                className="mt-6 p-4 rounded-xl text-sm"
                style={{ backgroundColor: `${primaryColor}08`, color: primaryColor }}
              >
                <div className="flex items-start gap-2">
                  <Droplets className="w-5 h-5 shrink-0 mt-0.5" />
                  <p>
                    <strong>Value:</strong> This treatment alone is worth $129/year. It's included free with your membership — just one more way we help you avoid expensive problems. And avoiding home damage or an AC shutdown from a clogged drain line is priceless.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
